'use client';

import Link from 'next/link';
import NewsletterForm from '@/components/NewsletterForm';

/**
 * SiteFooter - the shared dark footer used on the homepage and across
 * marketing pages (about, contact, services, etc.). Extracted from
 * ModernHomePage so it stays consistent everywhere.
 */
const SiteFooter = () => {
  const currentYear = new Date().getFullYear();

  const productLinks = [
    { label: 'Features', href: '/homepage' },
    { label: 'Pricing', href: '/homepage#pricing' },
    { label: 'Reviews', href: '/homepage#reviews' },
    { label: 'Updates', href: '/blog' },
  ];

  const serviceLinks = [
    { label: 'HDR Processing', href: '/services/image-enhancement' },
    { label: 'Day to Dusk', href: '/services/day-to-dusk' },
    { label: 'Virtual Staging', href: '/services/virtual-staging' },
    { label: 'Item Removal', href: '/services/item-removal' },
    { label: 'And More', href: '/services' },
  ];

  const companyLinks = [
    { label: 'About Us', href: '/about' },
    { label: 'Careers', href: '/careers' },
    { label: 'Contact', href: '/contact' },
    { label: 'Blog', href: '/blog' },
    { label: 'Privacy Policy', href: '/privacy' },
  ];

  const socials = [
    { label: 'facebook', href: '#' },
    { label: 'twitter', href: '#' },
    { label: 'linkedin', href: '#' },
    { label: 'instagram', href: '#' },
  ];

  return (
    <footer className="bg-[#0B0F1A] border-t border-gray-800 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/shashio-logo.svg" alt="Shashio" className="h-8 w-auto" />
            </div>
            <p className="text-gray-400 text-sm mb-6 max-w-xs">
              Professional real estate photo editing services that help you win more listings and grow your business.
            </p>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-white font-semibold mb-4">Product</h3>
            <ul className="space-y-2">
              {productLinks.map((item) => (
                <li key={item.label}>
                  <Link href={item.href} className="text-gray-400 hover:text-white text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              {serviceLinks.map((item) => (
                <li key={item.label}>
                  <Link href={item.href} className="text-gray-400 hover:text-white text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              {companyLinks.map((item) => (
                <li key={item.label}>
                  <Link href={item.href} className="text-gray-400 hover:text-white text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-white font-semibold mb-1">Stay Updated</h3>
              <p className="text-gray-400 text-sm">Get tips, tutorials, and special offers.</p>
            </div>
            <NewsletterForm variant="footer" source="footer" />
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-800">
          <p className="text-gray-400 text-sm">© {currentYear} Shashio Inc. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms of Service</Link>
            <Link href="/cookies" className="text-gray-400 hover:text-white text-sm transition-colors">Cookie Policy</Link>
          </div>
          <div className="flex items-center gap-3">
            {socials.map((social) => (
              <Link
                key={social.label}
                href={social.href}
                aria-label={social.label}
                className="w-8 h-8 bg-[#1A1F35] hover:bg-purple-600 rounded-lg flex items-center justify-center transition-colors group"
              >
                <span className="text-gray-400 group-hover:text-white text-xs">{social.label[0].toUpperCase()}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
